import { TaxonomyFileFormData } from '@utils/schemas';
import { find, map } from 'lodash';
import { Taxonomy } from '@/types/Taxonomy';
import { PermissionType } from '@/types/auth';
import { PermissionFromStore, permissions, UserRole } from '@/types/types';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useCanCreateOrganizationStore } from '@/stores/canCreateOrganizationStore';
import { FirestoreError } from 'firebase/firestore';
import { ERROR_MESSAGES } from './constants';
import { sanitizeCallableErrorMessage } from './sanitizeCallableErrorMessage';

export function isFetcherLoading(state: 'idle' | 'loading' | 'submitting') {
  return ['loading', 'submitting'].includes(state);
}

export function sleep(time: number, rejectArg = false, callback?: () => void) {
  return new Promise((resolve, reject) =>
    setTimeout(() => {
      if (rejectArg) {
        reject();
      } else {
        resolve(callback);
      }
    }, time),
  );
}

export function sanitizeBaseLink(baseLink: string): string {
  const isValidLink = baseLink.endsWith('/?');
  if (isValidLink || baseLink.length === 0) {
    return baseLink;
  }

  const isEndingWithSlash = baseLink.endsWith('/');
  if (isEndingWithSlash) {
    return baseLink + '?';
  }

  return baseLink + '/?';
}

export function groupTaxonomy(data: TaxonomyFileFormData) {
  const groupedTaxonomy = new Map<string, Taxonomy>();
  let taxonomyGroupsCreated = 0;
  data.file.forEach((taxonomy) => {
    if (groupedTaxonomy.has(taxonomy.queryParameter)) {
      const currentTaxonomy = groupedTaxonomy.get(taxonomy.queryParameter) as Taxonomy;
      currentTaxonomy.params?.push({
        prefix: taxonomy.prefix,
        description: taxonomy.description,
        name: taxonomy.parameterName,
      });
    } else {
      const newTaxonomy: Taxonomy = {
        queryParameter: taxonomy.queryParameter,
        order: taxonomyGroupsCreated,
        params: [
          {
            prefix: taxonomy.prefix,
            description: taxonomy.description,
            name: taxonomy.parameterName,
          },
        ],
      };
      taxonomyGroupsCreated += 1;
      groupedTaxonomy.set(taxonomy.queryParameter, newTaxonomy);
    }
  });
  return groupedTaxonomy;
}

export function useGetPermissionsLevels(permissionType: PermissionType) {
  const allRoles = Object.keys(permissions);
  return map(allRoles, (role) => {
    return isPermissionSufficient(role as UserRole, permissionType);
  });
}

export function useIsPermissionSufficient(
  neededRole: UserRole,
  permissionType: PermissionType = PermissionType.Workspace,
) {
  const workspacePermission = useUserPermissionsStore(
    (state) => state.workspacePermission,
  );
  const organizationPermission = useUserPermissionsStore(
    (state) => state.organizationPermission,
  );

  return checkAccess(
    permissionType,
    neededRole,
    (workspacePermission as PermissionFromStore).role,
    (organizationPermission as PermissionFromStore).role,
  );
}

export function isPermissionSufficient(
  neededRole: UserRole,
  permissionType: PermissionType = PermissionType.Workspace,
  override: null | {
    workspaceRole: UserRole | null;
    organizationRole: UserRole | null;
  } = null,
) {
  const workspacesRole =
    override?.workspaceRole ||
    useUserPermissionsStore.getState().workspacePermission?.role;
  const organizationRole =
    override?.organizationRole ||
    useUserPermissionsStore.getState().organizationPermission?.role;

  return checkAccess(permissionType, neededRole, workspacesRole, organizationRole);
}

function checkAccess(
  permissionType: PermissionType,
  neededRole: UserRole,
  workspacesRole: UserRole | null | undefined,
  organizationRole: UserRole | null | undefined,
) {
  let isPermissionSufficient = false;
  if (permissionType === PermissionType.Both) {
    isPermissionSufficient = Boolean(
      workspacesRole &&
        isProperPermission(neededRole, workspacesRole) &&
        organizationRole &&
        isProperPermission(neededRole, organizationRole),
    );
  } else {
    isPermissionSufficient = Boolean(
      permissionType === PermissionType.Workspace
        ? workspacesRole && isProperPermission(neededRole, workspacesRole)
        : organizationRole && isProperPermission(neededRole, organizationRole),
    );
  }

  return isPermissionSufficient;
}

export function isProperPermission(minumumRole: UserRole, userRole: UserRole) {
  return permissions[userRole] >= permissions[minumumRole];
}

export function isWorkspaceInOrganization(workspaceId: string | null): boolean {
  const selectedOrganization = useOrganizationStore.getState().selectedOrganization;
  if (!selectedOrganization || !workspaceId) return false;

  return !!find(selectedOrganization.workspaces, { id: workspaceId });
}

export function formDataToObject<T extends Record<string, unknown>>(data: FormData): T {
  const obj = Object.fromEntries(data.entries()) as Record<string, unknown>;

  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (typeof v === 'string') {
      const s = v.trim();
      if (
        (s.startsWith('{') && s.endsWith('}')) ||
        (s.startsWith('[') && s.endsWith(']'))
      ) {
        try {
          obj[k] = JSON.parse(s);
        } catch (e) {
          console.error(e);
        }
      } else if (s === 'true' || s === 'false') {
        obj[k] = s === 'true';
      } else if (s === 'null') {
        obj[k] = null;
      }
    }
  }

  return obj as T;
}

export function canCreateOrganizations(): boolean {
  const canUserCreateOrganizations =
    useCanCreateOrganizationStore.getState().canCreateOrganization;
  const isOrganizationCreationWhitelisted =
    import.meta.env._VITE_IS_ORGANIZATION_CREATION_WHITELISTED === 'true';

  return (
    !isOrganizationCreationWhitelisted ||
    (isOrganizationCreationWhitelisted && canUserCreateOrganizations)
  );
}

export function isObjectKey(
  key: string | undefined,
  object: Record<string, unknown>,
): key is keyof typeof object {
  if (!key) return false;
  return key in object;
}

export function mapErrorMessage(error: unknown): string {
  if (error instanceof FirestoreError) {
    return 'Failed to create an invitation. Try again later!';
  } else if (error instanceof Error) {
    const safeMessage = sanitizeCallableErrorMessage(error);
    return ERROR_MESSAGES[safeMessage] ?? safeMessage;
  }
  return 'Unknown error';
}
